import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';

const FREE_PERIOD_END = new Date('2026-09-26T23:59:59');

export const isFreePeriod = () => {
  return new Date() < FREE_PERIOD_END;
};

export const getFreePeriodEndDate = () => FREE_PERIOD_END;

// Routes where the LWT free-period banner must NOT render — primarily the
// Trust Hub corporate gateway, which targets partners + visitors who haven't
// been granted LWT access yet.
const HIDDEN_ON_PATHS = ['/', '/trust-hub'];

const FreePeriodBanner = () => {
  const { language, localize } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [remainingDays, setRemainingDays] = useState(0);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('free_banner_dismissed');
    if (wasDismissed) setDismissed(true);

    const update = () => {
      const now = new Date();
      const diff = FREE_PERIOD_END - now;
      if (diff <= 0) {
        setExpired(true);
        return;
      }
      setRemainingDays(Math.ceil(diff / (1000 * 60 * 60 * 24)));
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, []);

  if (dismissed || expired) return null;
  // Hide on corporate hub & landing root — those audiences haven't been
  // granted LWT access yet and shouldn't see a tutoring-specific promo.
  if (HIDDEN_ON_PATHS.includes(location.pathname)) return null;

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('free_banner_dismissed', 'true');
  };

  return (
    <div
      onClick={() => navigate('/free')}
      data-testid="free-period-banner"
      className="relative z-[40] cursor-pointer border-b border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] backdrop-blur"
    >
      <div className="container mx-auto flex items-center justify-center gap-3 px-4 py-2 text-xs sm:text-[13px] font-medium">
        {/* Subtle pulsing indigo dot — institutional "live" indicator */}
        <span
          className="relative flex items-center justify-center"
          aria-hidden
        >
          <span className="absolute inset-0 rounded-full bg-[var(--accent-primary)]/40 animate-ping" style={{ animationDuration: '2.4s' }} />
          <span className="relative h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)]" />
        </span>
        <span className="text-[var(--text-secondary)]">
          {localize({
            ar: `عرض الافتتاح الخاص: 100٪ مجاناً حتى 26 سبتمبر 2026 · ${remainingDays} يوم متبقي`,
            en: `Special Opening Offer: 100% Free until September 26, 2026 · ${remainingDays} days remaining`,
          })}
        </span>
        <span
          className="text-[var(--accent-primary)] font-semibold hover:text-[var(--accent-primary-hover)] transition-colors inline-flex items-center gap-1"
        >
          {localize({ ar: 'استفد الآن', en: 'Claim' })} →
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'left-3' : 'right-3'} hover:bg-[var(--border-color)] rounded-full p-1 transition-colors text-[var(--text-tertiary)] hover:text-[var(--text-primary)]`}
          data-testid="dismiss-free-banner"
          aria-label="Dismiss banner"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default FreePeriodBanner;
